import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import {
  ActivityItem,
  ActivityType,
  ActivityFilterType,
  ModuleFilterType,
  ActivityGroup,
  ROUTE_MAPPINGS,
} from './activity-panel.types';
import { BackendApiService } from '../../../../services/backend-api.service';
import { UserService } from '../../../../services/core/user.service';

const STORAGE_KEY = 'prosuite_user_activity';
const CLEARED_LAST_ID_KEY = 'prosuite_activity_cleared_last_id';
const PINNED_STATE_KEY = 'prosuite_activity_pinned_state';
const MAX_ITEMS = 50;
const MODULE_FILTERS: ModuleFilterType[] = ['bids', 'takeoffs', 'proposals', 'jobs', 'customers', 'vendors', 'staff'];

/**
 * Tracks user navigation and actions for the Activity Panel.
 * Persists to localStorage; listens to Router NavigationEnd events.
 */
@Injectable({ providedIn: 'root' })
export class HeaderActivityService {
  private activitiesSubject = new BehaviorSubject<ActivityItem[]>([]);

  /** Observable stream of all stored activities. */
  activities$: Observable<ActivityItem[]> = this.activitiesSubject.asObservable();

  /** Observable of the total activity count. */
  totalCount$: Observable<number> = this.activities$.pipe(map(a => a.length));

  private routerSubscribed = false;

  constructor(
    private router: Router,
    private backendApiService: BackendApiService,
    private userService: UserService,
  ) {
    this.loadFromStorage();
  }

  /**
   * Start listening to router navigation events.
   * Call once from the header component.
   */
  startRouterTracking(): void {
    if (this.routerSubscribed) return;
    this.routerSubscribed = true;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => this.trackPageVisit(event.urlAfterRedirects || event.url));
  }

  /**
   * Record a page visit from a router URL.
   * Deduplicates consecutive visits to the same URL.
   */
  trackPageVisit(url: string): void {
    const parsed = this.parseRoute(url);
    if (!parsed) return;

    const current = this.activitiesSubject.getValue();
    // Skip if the most recent non-pinned activity is the same URL
    const lastVisit = current.find(a => a.type === 'page_visit');
    if (lastVisit && lastVisit.title === parsed.title && lastVisit.subtitle === parsed.subtitle) {
      return;
    }

    const item: ActivityItem = {
      id: this.generateId(),
      type: 'page_visit',
      title: parsed.title,
      subtitle: parsed.subtitle,
      timestamp: Date.now(),
      pinned: false,
      iconType: parsed.icon,
    };

    this.addActivity(item);
  }

  /**
   * Record a user action (approve, create, update, etc.).
   */
  trackAction(title: string, subtitle: string, icon: string = 'check_circle'): void {
    this.addActivity({
      id: this.generateId(),
      type: 'action',
      title,
      subtitle,
      timestamp: Date.now(),
      pinned: false,
      iconType: icon,
    });
  }

  /**
   * Record a search query.
   */
  trackSearch(query: string, context: string = 'Global Search'): void {
    this.addActivity({
      id: this.generateId(),
      type: 'search',
      title: `"${query}"`,
      subtitle: context,
      timestamp: Date.now(),
      pinned: false,
      iconType: 'search',
    });
  }

  /** Soft-delete or restore an activity log and hide deleted items from list. */
  toggleDelete(id: string, isDeleted: boolean): Observable<boolean> {
    return this.backendApiService
      .postApi(
        `activity/log/${id}/delete`,
        { exclude_deleted: true },
        false
      )
      .pipe(
        map(() => {
          this.setPinnedOverride(id, false, isDeleted);
          const updated = this.activitiesSubject.getValue()
            .map(a => (a.id === id ? { ...a, deleted: isDeleted } : a)) // update the deleted flag for the item
            .filter(a => !a.deleted);     // remove deleted items from the list
          this.activitiesSubject.next(updated); // update the list with the new item
          return true;
        }),
        catchError(() => of(false))
      );
  }

  /** Persist pin/unpin for an activity log and update local state. */
  togglePin(id: string, isPinned: boolean): Observable<boolean> {
    return this.backendApiService
      .postApi(`activity/log/${id}/pin`, { is_pinned: isPinned }, false)
      .pipe(
        map(() => {   // update the pinned flag for the item
          this.setPinnedOverride(id, isPinned); 
          const updated = this.activitiesSubject.getValue().map(a =>
            a.id === id ? { ...a, pinned: isPinned } : a // update the pinned flag for the item
          );
          this.activitiesSubject.next(updated); // update the list with the new item
          return true;
        }),
        catchError(() => of(false))
      );
  }

  /** Clear all activities (optionally preserving pinned). */
  clearAll(preservePinned: boolean = false): void {
    const updated = preservePinned
      ? this.activitiesSubject.getValue().filter(a => a.pinned)
      : [];
    this.activitiesSubject.next(updated);
    this.saveToStorage(updated);
  }

  /** Clear all activity history on backend and reset local panel state. */
  clearHistory(): Observable<boolean> {
    return this.backendApiService
      .postApi('activity/log/clear-history', {}, false)
      .pipe(
        map(() => {
          this.activitiesSubject.next([]);   
          return true;
        }),
        catchError(() => of(false))
      );
  }

  /**
   * Filter and group activities for display.
   */
  getFilteredGrouped(
    activities: ActivityItem[],
    filterType: ActivityFilterType,
    searchQuery: string
  ): ActivityGroup[] {
    let result = activities;

    if (filterType !== 'all') {
      // Module chips are filtered at API level via module_id.
      // Keep current list unchanged for those chips.
      if (!MODULE_FILTERS.includes(filterType as ModuleFilterType)) {
        result = result.filter(a => a.type === filterType);
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        a => a.title.toLowerCase().includes(q) || a.subtitle.toLowerCase().includes(q)
      );
    }

    // Sort: pinned first, then by timestamp descending
    result = [...result].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return b.timestamp - a.timestamp;
    });

    return this.groupByDate(result);
  }

  /**
   * Fetch the latest 5 activity logs for the current user from the API.
   */
  fetchApiActivities(
    search: string = '',
    page: number = 1,
    moduleId?: number,
    subjectId?: number
  ): Observable<{ items: ActivityItem[], hasMore: boolean }> {
    const perPage = 12;
    const userId = this.userService.getID();
    const params = {
      staff_id: userId,
      sort_by: 'id',
      sort_order: 'DESC',
      per_page: perPage,
      // Send as numeric boolean in query params for backend validators.
      exclude_deleted: 1,
      search,
      page,
    };
    if (moduleId !== undefined && moduleId !== null) {
      (params as any).module_id = moduleId;
    }
    if (subjectId !== undefined && subjectId !== null) {
      (params as any).subject_id = subjectId;
    }
    const clearedLastId = this.getClearedLastId();
    return this.backendApiService.getApi('activity/log', params, false).pipe(
      map((response: any) => {
        const logs: any[] = response?.data?.data || response?.data || [];
        const pinnedOverrides = this.getPinnedOverrides();
        let items = logs
          .filter((log: any) => !this.getBooleanFlag(log, 'is_deleted'))   // remove deleted items from the list
          .map((log: any) => this.mapApiLogToActivityItem(log, pinnedOverrides));
        if (clearedLastId !== null) { 
          items = items.filter(item => Number(item.id) > clearedLastId); // remove items that are older than the cleared last id
        }
        const lastPage = response?.data?.last_page;
        const hasMore = lastPage ? page < lastPage : items.length === perPage;
        return { items, hasMore }; 
      }),
      catchError(() => of({ items: [], hasMore: false }))
    );
  }

  /** Store the latest Activity_id as the cleared marker. Only records with a higher id will be shown. */
  setClearedLastId(latestActivityId: number): void {
    try {
      localStorage.setItem(CLEARED_LAST_ID_KEY, String(latestActivityId));
    } catch {
      console.warn('[HeaderActivity] Failed to save cleared_last_id');
    }
  }

  private getClearedLastId(): number | null {
    try {
      const raw = localStorage.getItem(CLEARED_LAST_ID_KEY);
      return raw !== null ? Number(raw) : null;
    } catch {
      return null;
    }
  }

  // -- Private helpers --

  /**
   * `created_at_raw` is the ISO 8601 timestamp — the only field safe to do date
   * math on. Its sibling `created_at` is rendered server-side in the tenant's
   * chosen date format (which may be `d/m/Y`, `F j, Y`, …) and is not parseable
   * by `Date`; feeding it here is what produced "Invalid Date".
   *
   * Returns NaN when the value is missing or unparseable, so callers can fall
   * back to the server's display string rather than showing a wrong time.
   */
  private parseTimestamp(rawDate: unknown): number {
    if (!rawDate) { return NaN; }
    return new Date(rawDate as string).getTime();
  }

  private mapApiLogToActivityItem(log: any, pinnedOverrides: Record<string, boolean> = {}): ActivityItem {
    const id = String(log.Activity_id);
    const pinnedFromApi = this.getBooleanFlag(log, 'is_pinned');
    const deletedFromApi = this.getBooleanFlag(log, 'is_deleted');
    const pinned = Object.prototype.hasOwnProperty.call(pinnedOverrides, id)
      ? Boolean(pinnedOverrides[id])
      : pinnedFromApi;

    const rawSubjectId = log.subject_id;
    const parsedSubjectId =
      rawSubjectId === null || rawSubjectId === undefined || rawSubjectId === ''
        ? undefined
        : Number(rawSubjectId);

    return {
      id,
      type: this.mapEventToType(log.event),
      title: log.log_name || log.event || 'Activity',
      subtitle: log.description || '',
      timestamp: this.parseTimestamp(log.created_at_raw),
      timestampLabel: log.created_at || undefined,
      pinned,
      deleted: deletedFromApi,
      iconType: this.mapEventToIcon(log.event),
      subjectType: log.subject_type || undefined,
      subjectId: Number.isFinite(parsedSubjectId as number) ? (parsedSubjectId as number) : undefined,
    };
  }

  /**
   * Resolve an activity's related-record route from its subjectType / subjectId.
   * Returns null if the activity has no linked record or the subject type is unmapped.
   * Used by the panel's click handler to deep-link into detail pages.
   */
  resolveActivityRoute(
    item: ActivityItem
  ): { path: string; queryParams?: Record<string, any> } | null {
    if (!item.subjectType || item.subjectId === undefined || item.subjectId === null) {
      return null;
    }

    const key = item.subjectType.replace(/^App\\Models\\/i, '').toLowerCase();
    const id = item.subjectId;

    const routeMap: Record<string, { path: string; queryParams?: Record<string, any> }> = {
      job:           { path: `/jobs/detail/${id}` },
      bid:           { path: `/bids/bid/${id}` },
      takeoff:       { path: `/takeoffs/edit/${id}` },
      customer:      { path: `/customers/customer/${id}` },
      proposal:      { path: `/proposals/detail/${id}` },
      changeorder:   { path: `/change-orders/view/${id}` },
      purchaseorder: { path: `/purchase-order/detail/${id}` },
      submittal:     { path: `/submittals/view/${id}` },
      vendor:        { path: `/vendors/vendor/${id}` },
      task:          { path: `/tasks`, queryParams: { taskId: id } },
      ticket:        { path: `/tickets`, queryParams: { ticketId: id } },
      // Note: child entities (Note) need parent context - out of scope for v1.
    };

    return routeMap[key] ?? null;
  }

  private getPinnedOverrides(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(PINNED_STATE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private setPinnedOverride(id: string, isPinned: boolean, remove: boolean = false): void {
    try {
      const current = this.getPinnedOverrides();
      if (remove) {
        delete current[id];
      } else {
        current[id] = isPinned;
      }
      localStorage.setItem(PINNED_STATE_KEY, JSON.stringify(current));
    } catch {
      console.warn('[HeaderActivity] Failed to persist pinned override');
    }
  }

  private getBooleanFlag(log: any, key: 'is_deleted' | 'is_pinned'): boolean {
    return this.toBoolean(
      log?.[key] ??
      log?.userActions?.[0]?.[key] ??
      false
    );
  }

  private toBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === '1' || normalized === 'true';
    }
    return false;
  }

  private mapEventToType(event: string): ActivityType {
    const e = (event || '').toLowerCase();
    if (e === 'viewed') return 'page_visit';
    if (e === 'created' || e === 'create') return 'action';
    if (e === 'updated' || e === 'update') return 'action';
    if (e === 'deleted' || e === 'delete') return 'action';
    return 'page_visit';
  }

  private mapEventToIcon(event: string): string {
    const e = (event || '').toLowerCase();
    if (e === 'viewed') return 'visibility';
    if (e === 'created' || e === 'create') return 'add_circle';
    if (e === 'updated' || e === 'update') return 'edit';
    if (e === 'deleted' || e === 'delete') return 'delete_outline';
    return 'history';
  }

  private addActivity(item: ActivityItem): void {
    let current = this.activitiesSubject.getValue();
    current = [item, ...current];

    // Prune to MAX_ITEMS, keeping pinned items
    if (current.length > MAX_ITEMS) {
      const pinned = current.filter(a => a.pinned);
      const unpinned = current.filter(a => !a.pinned);
      current = [...pinned, ...unpinned].slice(0, MAX_ITEMS);
    }

    this.activitiesSubject.next(current);
    this.saveToStorage(current);
  }

  private parseRoute(url: string): { title: string; subtitle: string; icon: string } | null {
    // Remove query params and fragment
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segments = cleanUrl.split('/').filter(Boolean);

    if (segments.length === 0) return null;

    const primary = segments[0];
    const mapping = ROUTE_MAPPINGS[primary];

    if (!mapping) return null;

    // Detail page detection (e.g., /customers/123)
    if (segments.length >= 2 && /^\d+$/.test(segments[1])) {
      const singularTitle = mapping.title.replace(/s$/, '');
      return {
        title: `${singularTitle}: #${segments[1]}`,
        subtitle: `${mapping.title} \u2192 Detail`,
        icon: mapping.icon,
      };
    }

    // Sub-route (e.g., /warehouses/receiving)
    if (segments.length >= 2) {
      const subSegment = segments[segments.length - 1];
      const subTitle = subSegment.charAt(0).toUpperCase() + subSegment.slice(1).replace(/-/g, ' ');
      return {
        title: `${mapping.title} \u2013 ${subTitle}`,
        subtitle: `${mapping.title} \u2192 ${subTitle}`,
        icon: mapping.icon,
      };
    }

    return {
      title: mapping.title,
      subtitle: `Home \u2192 ${mapping.title}`,
      icon: mapping.icon,
    };
  }

  private groupByDate(items: ActivityItem[]): ActivityGroup[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterday = today - 86400000;
    const weekAgo = today - 86400000 * 7;

    const groups: Record<string, ActivityItem[]> = {};
    const order = ['Today', 'Yesterday', 'This Week', 'Older'];

    for (const item of items) {
      let group: string;
      if (item.timestamp >= today) group = 'Today';
      else if (item.timestamp >= yesterday) group = 'Yesterday';
      else if (item.timestamp >= weekAgo) group = 'This Week';
      else group = 'Older';

      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    }

    return order
      .filter(g => groups[g] && groups[g].length > 0)
      .map(g => ({ label: g, items: groups[g] }));
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed: ActivityItem[] = JSON.parse(raw);
        this.activitiesSubject.next(parsed);
      }
    } catch {
      console.warn('[HeaderActivity] Failed to load from localStorage');
    }
  }

  private saveToStorage(items: ActivityItem[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      console.warn('[HeaderActivity] Failed to save to localStorage');
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
  }
}
